import { FunctionsHttpError } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

export interface Album {
  id: string;
  classId: string;
  title: string;
  takenOn: string | null;
  createdAt: string;
  photos: number;
  cover: string | null;
}

export interface AlbumPhoto {
  id: string;
  path: string;
  width: number | null;
  height: number | null;
  caption: string | null;
  createdAt: string;
  addedBy: string | null;
  /** a signed link, valid for ten minutes */
  url: string | null;
}

const LINK_SECONDS = 600;

export function useAlbums(classId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['albums', classId],
    enabled: classId !== undefined && classId !== '',
    queryFn: async () => {
      const rows = must(
        await db
          .from('albums')
          .select(
            'id, class_id, title, taken_on, created_at, album_photos (storage_path, created_at)',
          )
          .eq('class_id', classId as string)
          .order('taken_on', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false }),
      );
      const covers = rows
        .map((a) => [...a.album_photos].sort((x, y) => x.created_at.localeCompare(y.created_at))[0])
        .filter((p) => p !== undefined)
        .map((p) => p.storage_path);
      const signed =
        covers.length > 0
          ? must(await db.storage.from('album').createSignedUrls(covers, LINK_SECONDS))
          : [];
      const link = new Map(signed.map((s) => [s.path, s.signedUrl]));
      return rows.map((a): Album => {
        const first = [...a.album_photos].sort((x, y) =>
          x.created_at.localeCompare(y.created_at),
        )[0];
        return {
          id: a.id,
          classId: a.class_id,
          title: a.title,
          takenOn: a.taken_on,
          createdAt: a.created_at,
          photos: a.album_photos.length,
          cover: first ? (link.get(first.storage_path) ?? null) : null,
        };
      });
    },
  });
}

export function useAlbumPhotos(albumId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['album-photos', albumId],
    enabled: albumId !== undefined,
    // links expire after ten minutes: fetch new ones before that
    staleTime: (LINK_SECONDS - 60) * 1000,
    queryFn: async () => {
      const rows = must(
        await db
          .from('album_photos')
          .select('id, storage_path, width, height, caption, created_at, added_by_member_id')
          .eq('album_id', albumId as string)
          .order('created_at'),
      );
      const signed =
        rows.length > 0
          ? must(
              await db.storage.from('album').createSignedUrls(
                rows.map((r) => r.storage_path),
                LINK_SECONDS,
              ),
            )
          : [];
      const link = new Map(signed.map((s) => [s.path, s.signedUrl]));
      return rows.map((r): AlbumPhoto => ({
        id: r.id,
        path: r.storage_path,
        width: r.width,
        height: r.height,
        caption: r.caption,
        createdAt: r.created_at,
        addedBy: r.added_by_member_id,
        url: link.get(r.storage_path) ?? null,
      }));
    },
  });
}

/** Children of the class who must not be in photos: not every guardian agreed. */
export function useWithoutPhotoConsent(classId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['without-photo-consent', classId],
    enabled: classId !== undefined && classId !== '',
    queryFn: async () =>
      must(await db.rpc('album_without_photo_consent', { p_class: classId as string })).map(
        (r) => `${r.first_name} ${r.last_name}`,
      ),
  });
}

export type AlbumUploadError = 'not_allowed' | 'too_large' | 'unsupported' | 'server_error';

export function useAlbumActions(schoolId: string, memberId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['albums'] });
    void client.invalidateQueries({ queryKey: ['album-photos'] });
  };
  return {
    create: useMutation({
      mutationFn: async (a: { classId: string; title: string; takenOn: string | null }) => {
        const { data, error } = await db
          .from('albums')
          .insert({
            school_id: schoolId,
            class_id: a.classId,
            title: a.title.trim(),
            taken_on: a.takenOn,
            created_by_member_id: memberId,
          })
          .select('id')
          .single();
        if (error) throw error;
        return data.id;
      },
      onSettled: invalidate,
    }),
    /** Through album-upload: the server removes every piece of metadata before storing. */
    upload: useMutation({
      mutationFn: async (a: { albumId: string; photo: Blob; caption?: string }) => {
        const form = new FormData();
        form.set('album', a.albumId);
        form.set('file', a.photo, 'photo.jpg');
        if (a.caption) form.set('caption', a.caption);
        const { error } = await db.functions.invoke('album-upload', { body: form });
        if (!error) return;
        if (error instanceof FunctionsHttpError) {
          const body = (await (error.context as Response).json().catch(() => ({}))) as {
            error?: AlbumUploadError;
          };
          throw new Error(body.error ?? 'server_error');
        }
        throw new Error('server_error');
      },
      onSettled: invalidate,
    }),
    removePhoto: useMutation({
      mutationFn: async (p: Pick<AlbumPhoto, 'id' | 'path'>) => {
        // the file first: its permission needs the row
        const { error } = await db.storage.from('album').remove([p.path]);
        if (error) throw error;
        must(await db.from('album_photos').delete().eq('id', p.id));
      },
      onSettled: invalidate,
    }),
    removeAlbum: useMutation({
      mutationFn: async (albumId: string) => {
        const paths = must(
          await db.from('album_photos').select('storage_path').eq('album_id', albumId),
        ).map((r) => r.storage_path);
        if (paths.length > 0) {
          const { error } = await db.storage.from('album').remove(paths);
          if (error) throw error;
        }
        must(await db.from('albums').delete().eq('id', albumId));
      },
      onSettled: invalidate,
    }),
  };
}
